/**
 * Which widget and dashboard a /api/metrics/sql-query request is for (PLAN A2/A3).
 *
 * The backend records the widget in dashboard_audit.widget_id and both ids in the
 * ClickHouse statement's log_comment, so per-widget cost can be read from query_log.
 * Until this was sent, widget_id was 0 on every audit row.
 *
 * Attribution only: the server never uses these ids to authorise anything, and an older
 * backend ignores them. Only persisted, positive integer ids are sent — a widget that
 * exists only in the designer (a client-generated key) is sent without one.
 *
 * The ids are added AFTER the request's dedup key is built (see withAttribution), so two
 * widgets running identical SQL still coalesce into one request, exactly as before; the
 * one request is then attributed to the first widget that asked.
 */

/** A positive safe integer from a number or a digit string, else undefined. */
export const persistedId = (raw) => {
  if (typeof raw === 'number') {
    return Number.isSafeInteger(raw) && raw > 0 ? raw : undefined;
  }
  if (typeof raw === 'string' && /^[1-9][0-9]{0,15}$/.test(raw)) {
    const n = Number(raw);
    return Number.isSafeInteger(n) ? n : undefined;
  }
  return undefined;
};

/** `{ widgetId, dashboardId }` with only the ids that are persisted. */
export const attributionIds = (widget, dashboard) => {
  const ids = {};
  const widgetId = persistedId(widget?.id);
  const dashboardId = persistedId(dashboard?.id);
  if (widgetId !== undefined) ids.widgetId = widgetId;
  if (dashboardId !== undefined) ids.dashboardId = dashboardId;
  return ids;
};

/** The request body to POST: the payload plus the ids. The payload itself is not changed. */
export const withAttribution = (payload, widget, dashboard) => ({
  ...payload,
  ...attributionIds(widget, dashboard),
});
